import {
  AVAILABLE_MODEL_VERSIONS,
  DEFAULT_CHARACTER2VIDEO_FORM_PARAMS,
  DEFAULT_FORM_PARAMS,
  DEFAULT_IMAGE_FORM_PARAMS,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
  SETTINGS_CONFIG,
  TASK_SETTING_OPTION,
} from '@/app/constants';
import type {
  FormImagePrompt,
  FormParams,
  FormTabType,
  Img2VideoFormParams,
  ModelVersion,
  ReferencePrompt,
  SupportedLanguage,
  TaskSettings,
  TextPromptWithInputItem,
} from '@/app/types';
import {
  appendUnMentionedReferences,
  getAvailableOptions,
  getDefaultModelVersionForReferenceMedia,
  getImg2VideoMaxImageCount,
  getNextImageName,
  getReferenceImageAndElementMaxCount,
  getReferenceVideoMaxCount,
  isReferenceVideoPrompt,
  isReferenceVideoSupported,
  parseTextToTextPromptWithReference,
  stringifyTextPromptWithReference,
  validateAndCorrectSettings,
} from '@/app/utils';

export type HomeGenerationMode = 'character2video' | 'img2video' | 'text2video' | 'reference2image' | 'text2image';
type HomeGenerationMediaInput = 'subject' | 'image';
export type HomeGenerationSettingKey =
  | Extract<
      keyof TaskSettings,
      | 'duration'
      | 'resolution'
      | 'aspect_ratio'
      | 'sample_count'
      | 'transition'
      | 'movement_amplitude'
      | 'style'
      | 'schedule_mode'
      | 'codec'
    >
  | 'style_preference'
  | 'audio_and_lip_sync';

export const HOME_GENERATION_MODES: readonly HomeGenerationMode[] = [
  'character2video',
  'img2video',
  'text2video',
  'reference2image',
  'text2image',
];

const HOME_REFERENCE_MODES = new Set<HomeGenerationMode>(['character2video', 'reference2image']);
const HOME_IMAGE_TO_VIDEO_MODE: HomeGenerationMode = 'img2video';

const MAX_IMG2VIDEO_CARRY_IMAGES = 7;
const HOME_DEFAULT_FORM_PARAMS_BY_MODE: Record<HomeGenerationMode, FormParams> = {
  character2video: DEFAULT_CHARACTER2VIDEO_FORM_PARAMS,
  img2video: DEFAULT_FORM_PARAMS,
  text2video: DEFAULT_FORM_PARAMS,
  reference2image: DEFAULT_IMAGE_FORM_PARAMS,
  text2image: DEFAULT_IMAGE_FORM_PARAMS,
};
const HOME_SETTING_OPTION_TO_KEYS: Partial<Record<TASK_SETTING_OPTION, HomeGenerationSettingKey[]>> = {
  [TASK_SETTING_OPTION.Style]: ['style'],
  [TASK_SETTING_OPTION.MovementAmplitude]: ['movement_amplitude'],
  [TASK_SETTING_OPTION.StylePreference]: ['style_preference'],
  [TASK_SETTING_OPTION.Duration]: ['duration'],
  [TASK_SETTING_OPTION.DurationAndResolution]: ['duration', 'resolution'],
  [TASK_SETTING_OPTION.Resolution]: ['resolution'],
  [TASK_SETTING_OPTION.ResolutionAndCodec]: ['resolution', 'codec'],
  [TASK_SETTING_OPTION.AspectRatio]: ['aspect_ratio'],
  [TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio]: ['movement_amplitude', 'aspect_ratio'],
  [TASK_SETTING_OPTION.Amount]: ['sample_count'],
  [TASK_SETTING_OPTION.CreationMode]: ['transition'],
  [TASK_SETTING_OPTION.AudioAndLipSync]: ['audio_and_lip_sync'],
  [TASK_SETTING_OPTION.NonPeakMode]: ['schedule_mode'],
  [TASK_SETTING_OPTION.Codec]: ['codec'],
};
export const isHomeReferenceMode = (mode: HomeGenerationMode) => HOME_REFERENCE_MODES.has(mode);

export const isHomeVideoMode = (mode: HomeGenerationMode) =>
  mode === 'character2video' || mode === 'img2video' || mode === 'text2video';

export const getDefaultModelVersionForHomeMode = (mode: HomeGenerationMode): ModelVersion =>
  HOME_DEFAULT_FORM_PARAMS_BY_MODE[mode].modelVersion;

/**
 * 首页表单在当前 mode 下要展示哪些媒体入口。
 *
 * 视频不在这里出现：character2video 的视频走 `shouldUseMixedReferenceMediaUpload`
 * 那条图片/视频混合上传路径，不占独立入口。
 */
export const getVisibleHomeMediaInputs = (mode: HomeGenerationMode): HomeGenerationMediaInput[] => {
  switch (mode) {
    case 'text2video':
    case 'text2image':
      return [];
    case 'img2video':
      return ['image'];
    case 'reference2image':
    case 'character2video':
      return ['subject', 'image'];
  }
};

export const getVisibleHomeSettingKeys = (
  mode: HomeGenerationMode,
  modelVersion: ModelVersion,
): HomeGenerationSettingKey[] => {
  const type = toFormTabType(mode);
  const settingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
  const seen = new Set<HomeGenerationSettingKey>();

  return (SETTINGS_CONFIG[type][settingsKey] || []).flatMap((option) => {
    const keys = HOME_SETTING_OPTION_TO_KEYS[option] || [];
    return keys.filter((key) => {
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  });
};

const HOME_COMPOSITE_SETTING_KEYS: HomeGenerationSettingKey[] = ['aspect_ratio', 'sample_count', 'resolution'];
/**
 * 桌面端 rail 上的独立 chip。当前为空：transition（生成模式）已收进 extra 的滑块 popover，
 * 与 codec 相邻。渲染分支保留，方便后续再把某个 setting 提到 rail 上。
 */
const HOME_DESKTOP_PRIMARY_SETTING_KEYS: HomeGenerationSettingKey[] = [];
const HOME_DESKTOP_SETTING_ORDER: HomeGenerationSettingKey[] = [
  ...HOME_DESKTOP_PRIMARY_SETTING_KEYS,
  ...HOME_COMPOSITE_SETTING_KEYS,
  'duration',
  'style',
  'movement_amplitude',
  'style_preference',
  'audio_and_lip_sync',
  'schedule_mode',
  // transition 紧邻 codec，两者在 extra popover 里挨着展示。
  'transition',
  'codec',
];

const sortHomeSettingKeys = (keys: HomeGenerationSettingKey[]): HomeGenerationSettingKey[] =>
  [...keys].sort((a, b) => {
    const aIndex = HOME_DESKTOP_SETTING_ORDER.indexOf(a);
    const bIndex = HOME_DESKTOP_SETTING_ORDER.indexOf(b);
    const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

    if (normalizedAIndex !== normalizedBIndex) {
      return normalizedAIndex - normalizedBIndex;
    }

    return keys.indexOf(a) - keys.indexOf(b);
  });

export const getHomeDesktopSettingsLayout = (
  keys: HomeGenerationSettingKey[],
): {
  primary: HomeGenerationSettingKey[];
  composite: HomeGenerationSettingKey[];
  duration?: HomeGenerationSettingKey;
  extra: HomeGenerationSettingKey[];
} => {
  const visibleKeys = new Set(keys);
  const primary = HOME_DESKTOP_PRIMARY_SETTING_KEYS.filter((key) => visibleKeys.has(key));
  const composite = HOME_COMPOSITE_SETTING_KEYS.filter((key) => visibleKeys.has(key));
  const duration = visibleKeys.has('duration') ? 'duration' : undefined;
  const groupedKeys = new Set([...primary, ...composite, ...(duration ? [duration] : [])]);
  const extra = sortHomeSettingKeys(keys.filter((key) => !groupedKeys.has(key)));

  return {
    primary,
    composite,
    duration,
    extra,
  };
};

/**
 * 移动端 sheet 里展示的 setting，按数组顺序铺开。
 *
 * 移动端是竖排 sheet，没有 rail 的 chip / popover 分组概念，所以顺序独立声明，
 * 不跟着桌面端分组变化走（例如 transition 在桌面端被收进 extra，移动端仍排第一）。
 */
const HOME_MOBILE_SETTING_ORDER: HomeGenerationSettingKey[] = [
  // Keep the mobile sheet as a single vertical list, but include every setting
  // that can be exposed by the corresponding standalone form. The order is a
  // stable presentation order; model capabilities still decide which keys are
  // actually visible for a given mode/version.
  'style',
  'movement_amplitude',
  'style_preference',
  'transition',
  'resolution',
  'aspect_ratio',
  'sample_count',
  'duration',
  'audio_and_lip_sync',
  'schedule_mode',
  'codec',
];

export const getHomeMobileSettingsLayout = (keys: HomeGenerationSettingKey[]): HomeGenerationSettingKey[] => {
  const visibleKeys = new Set(keys);
  return HOME_MOBILE_SETTING_ORDER.filter((key) => visibleKeys.has(key));
};

const isReferenceSupportedByHomeMode = (mode: HomeGenerationMode, reference: ReferencePrompt) => {
  if (mode !== 'reference2image') {
    return true;
  }

  if (reference.type === 'video') {
    return false;
  }

  if (reference.type === 'element') {
    return !reference.prompt || !('modality' in reference.prompt) || reference.prompt.modality !== 'video';
  }

  return true;
};

export const getPlainPromptForHomeMode = (mode: HomeGenerationMode, formParams: FormParams) => {
  if (isHomeReferenceMode(mode)) {
    return stringifyTextPromptWithReference(formParams.textPromptWithReference || []);
  }

  return formParams.textPrompt || '';
};

const getReferencePromptsForHomeMode = (
  mode: HomeGenerationMode,
  formParams: FormParams,
  locale: SupportedLanguage = 'en',
): ReferencePrompt[] => {
  if (isHomeReferenceMode(mode)) {
    return formParams.referencePrompts || [];
  }

  if (mode === HOME_IMAGE_TO_VIDEO_MODE) {
    return (formParams.imagePrompts || [])
      .filter((item): item is FormImagePrompt => !!item?.prompt)
      .map(
        (item, index): ReferencePrompt => ({
          id: item.id,
          name: getNextImageName(index + 1, locale),
          status: item.status,
          type: 'image',
          prompt: item.prompt,
        }),
      );
  }

  return [];
};

const getReferenceImageAndElementPrompts = (references: ReferencePrompt[]) =>
  references.filter((reference) => !isReferenceVideoPrompt(reference));

const mergeReferencePrompts = (preserved: ReferencePrompt[], current: ReferencePrompt[]): ReferencePrompt[] => {
  const currentById = new Map(current.map((reference) => [reference.id, reference]));
  const seenIds = new Set<string>();
  const merged: ReferencePrompt[] = [];

  for (const reference of preserved) {
    const currentReference = currentById.get(reference.id);
    // Images are owned by the current image-to-video draft. A deleted image must not be restored.
    if (reference.type === 'image') {
      if (!currentReference) continue;
      merged.push(currentReference);
    } else {
      merged.push(reference);
    }
    seenIds.add(reference.id);
  }

  for (const reference of current) {
    if (!seenIds.has(reference.id)) {
      merged.push(reference);
      seenIds.add(reference.id);
    }
  }

  return merged;
};

export const buildReferencePromptPatch = ({
  text,
  referencePrompts,
  mode = 'character2video',
}: {
  text: string;
  referencePrompts: ReferencePrompt[];
  mode?: HomeGenerationMode;
}): {
  textPrompt: string;
  referencePrompts: ReferencePrompt[];
  textPromptWithReference: TextPromptWithInputItem;
} => {
  const supportedReferencePrompts = referencePrompts.filter((reference) =>
    isReferenceSupportedByHomeMode(mode, reference),
  );
  const parsed = parseTextToTextPromptWithReference(text, supportedReferencePrompts);
  return {
    textPrompt: '',
    referencePrompts: supportedReferencePrompts,
    textPromptWithReference: appendUnMentionedReferences(parsed, supportedReferencePrompts),
  };
};

export const buildPlainPromptPatch = (text: string): Pick<FormParams, 'textPrompt' | 'textPromptWithReference'> => ({
  textPrompt: text,
  textPromptWithReference: [],
});

export const buildImg2VideoPatch = ({
  text,
  referencePrompts,
}: {
  text: string;
  referencePrompts: ReferencePrompt[];
}): Pick<FormParams, 'textPrompt' | 'imagePrompts' | 'textPromptWithReference'> => {
  const imagePrompts = referencePrompts
    .filter((item): item is ReferencePrompt & { type: 'image' } => item.type === 'image' && !!item.prompt)
    .slice(0, MAX_IMG2VIDEO_CARRY_IMAGES)
    .map(
      (item): FormImagePrompt => ({
        id: item.id,
        status: item.status === 'uploading' ? 'uploading' : 'done',
        prompt: item.prompt,
      }),
    );

  return {
    textPrompt: text,
    imagePrompts,
    textPromptWithReference: [],
  };
};

export const buildHomeModeMediaPatch = ({
  text,
  sourceMode,
  targetMode,
  sourceFormParams,
  targetFormParams,
  locale,
  preservedReferencePrompts,
}: {
  text: string;
  sourceMode: HomeGenerationMode;
  targetMode: HomeGenerationMode;
  sourceFormParams: FormParams;
  targetFormParams?: FormParams;
  locale: SupportedLanguage;
  preservedReferencePrompts?: ReferencePrompt[];
}): Pick<FormParams, 'textPrompt' | 'textPromptWithReference'> &
  Partial<Pick<FormParams, 'imagePrompts' | 'referencePrompts'>> &
  Partial<Pick<Img2VideoFormParams, 'multiFrameTransitions'>> => {
  if (isHomeReferenceMode(targetMode)) {
    const currentReferencePrompts = getReferencePromptsForHomeMode(sourceMode, sourceFormParams, locale);
    const referencePrompts = preservedReferencePrompts
      ? sourceMode === HOME_IMAGE_TO_VIDEO_MODE
        ? mergeReferencePrompts(preservedReferencePrompts, currentReferencePrompts)
        : preservedReferencePrompts
      : sourceMode === 'text2video' || sourceMode === 'text2image'
        ? targetFormParams?.referencePrompts || []
        : currentReferencePrompts;

    return {
      ...buildReferencePromptPatch({
        text,
        referencePrompts,
        mode: targetMode,
      }),
      // 首页切换以当前编辑稿为准，不能残留目标生成方式历史的图生素材。
      imagePrompts: [],
    };
  }

  if (targetMode === HOME_IMAGE_TO_VIDEO_MODE) {
    return {
      ...buildImg2VideoPatch({
        text,
        referencePrompts: getReferencePromptsForHomeMode(sourceMode, sourceFormParams, locale),
      }),
      referencePrompts: [],
      multiFrameTransitions: undefined,
    };
  }

  return {
    ...buildPlainPromptPatch(text),
    imagePrompts: [],
    referencePrompts: [],
  };
};

export const applyHomeImg2VideoSnapshot = ({
  sourceMode,
  mediaPatch,
  snapshot,
}: {
  sourceMode: HomeGenerationMode;
  mediaPatch: ReturnType<typeof buildHomeModeMediaPatch>;
  snapshot: {
    images: FormImagePrompt[];
    multiFrameTransitions?: Img2VideoFormParams['multiFrameTransitions'];
  };
}): ReturnType<typeof buildHomeModeMediaPatch> => {
  if (!isHomeReferenceMode(sourceMode)) {
    return {
      ...mediaPatch,
      imagePrompts: snapshot.images,
      multiFrameTransitions: snapshot.multiFrameTransitions,
    };
  }

  const currentImages = mediaPatch.imagePrompts || [];
  const hasSameImageOrder =
    currentImages.length === snapshot.images.length &&
    currentImages.every((image, index) => image?.id === snapshot.images[index]?.id);

  return hasSameImageOrder ? { ...mediaPatch, multiFrameTransitions: snapshot.multiFrameTransitions } : mediaPatch;
};

export const normalizeHomeGenerationMode = (value: string | undefined): HomeGenerationMode => {
  if (value && HOME_GENERATION_MODES.includes(value as HomeGenerationMode)) {
    return value as HomeGenerationMode;
  }

  return 'text2video';
};

export const toFormTabType = (mode: HomeGenerationMode): Exclude<FormTabType, 'scene'> => mode;

const resolveHomeModeModelVersion = ({
  sourceModelVersion,
  targetFormParams,
  targetMode,
}: {
  sourceModelVersion: ModelVersion;
  targetFormParams: FormParams;
  targetMode: HomeGenerationMode;
}): ModelVersion => {
  const targetAvailableModelVersions = AVAILABLE_MODEL_VERSIONS[toFormTabType(targetMode)];
  const targetReferences =
    targetMode === 'img2video' ? [] : getReferencePromptsForHomeMode(targetMode, targetFormParams);
  const mediaCount =
    targetMode === 'img2video'
      ? targetFormParams.imagePrompts.filter(Boolean).length
      : isHomeReferenceMode(targetMode)
        ? getReferenceImageAndElementPrompts(targetReferences).length
        : 0;
  const videoCount = targetReferences.filter(isReferenceVideoPrompt).length;
  if (
    targetMode === 'img2video' &&
    mediaCount > 1 &&
    sourceModelVersion === MODEL_VERSION.Q3_HH &&
    targetAvailableModelVersions.includes(MODEL_VERSION.Q2)
  ) {
    return MODEL_VERSION.Q2;
  }
  const candidates = [sourceModelVersion, targetFormParams.modelVersion, getDefaultModelVersionForHomeMode(targetMode)];
  const compatibleCandidate = candidates.find((candidate) => {
    if (!targetAvailableModelVersions.includes(candidate)) return false;
    if (targetMode === 'img2video') return mediaCount <= getImg2VideoMaxImageCount(candidate);
    if (isHomeReferenceMode(targetMode)) {
      if (mediaCount > getReferenceImageAndElementMaxCount(candidate)) return false;
      if (targetMode === 'character2video' && videoCount > 0) {
        return (
          isReferenceVideoSupported(candidate) && videoCount <= getReferenceVideoMaxCount(candidate, targetReferences)
        );
      }
    }
    return true;
  });

  if (compatibleCandidate) return compatibleCandidate;
  if (targetMode === 'img2video' && mediaCount <= getImg2VideoMaxImageCount(MODEL_VERSION.Q2)) {
    return MODEL_VERSION.Q2;
  }

  if (targetMode === 'character2video' && videoCount > 0) {
    const videoModelVersion = getDefaultModelVersionForReferenceMedia('character2video', 'video');
    if (videoModelVersion && targetAvailableModelVersions.includes(videoModelVersion)) {
      return videoModelVersion;
    }
  }

  return targetMode === 'img2video'
    ? getDefaultModelVersionForHomeMode(targetMode)
    : candidates[candidates.length - 1] || getDefaultModelVersionForHomeMode(targetMode);
};

const canCarryHomeSetting = ({
  key,
  value,
  options,
  targetModelVersion,
}: {
  key: keyof TaskSettings;
  value: unknown;
  options: Record<string, readonly string[]>;
  targetModelVersion: ModelVersion;
}) => {
  const availableValues = options[key];
  if (!availableValues) return true;
  if (key === 'duration' && targetModelVersion === MODEL_VERSION.Q2_PRO && value === -1) return true;
  const valueString = String(value);
  if (availableValues.includes(valueString)) return true;
  if (key !== 'aspect_ratio' || !availableValues.includes('custom')) return false;
  const [wide, high] = valueString.split(':').map(Number.parseFloat);
  return Number.isFinite(wide) && Number.isFinite(high) && wide > 0 && high > 0;
};

export const buildSettingsCarryPatch = ({
  sourceFormParams,
  targetFormParams,
  targetMode,
  t,
}: {
  sourceFormParams: FormParams;
  targetFormParams: FormParams;
  targetMode: HomeGenerationMode;
  t?: Parameters<typeof validateAndCorrectSettings>[0]['t'];
}): Partial<FormParams> => {
  const sourceModelVersion = sourceFormParams.modelVersion;
  const targetModelVersion = resolveHomeModeModelVersion({
    sourceModelVersion,
    targetFormParams,
    targetMode,
  });
  const sourceSettings = sourceFormParams[MODEL_VERSION_SETTINGS_MAP[sourceFormParams.modelVersion]];
  const targetSettingsKey = MODEL_VERSION_SETTINGS_MAP[targetModelVersion];
  const targetSettings = targetFormParams[targetSettingsKey];
  const sourceSettingsRecord = sourceSettings as Record<keyof TaskSettings, unknown>;
  const targetSettingsRecord = targetSettings as Record<keyof TaskSettings, unknown>;
  const availableOptions = getAvailableOptions(targetModelVersion, toFormTabType(targetMode), {
    transition: String(targetSettingsRecord.transition ?? ''),
  });
  const commonSettings = Object.fromEntries(
    (Object.keys(sourceSettingsRecord) as Array<keyof TaskSettings>)
      .filter((key) => key in targetSettingsRecord)
      .map((key) => {
        const sourceValue = sourceSettingsRecord[key];
        if (canCarryHomeSetting({ key, value: sourceValue, options: availableOptions, targetModelVersion })) {
          return [key, sourceValue];
        }

        const availableValues = availableOptions[key];
        if (!availableValues || !['duration', 'resolution', 'sample_count'].includes(key)) {
          return [key, undefined];
        }

        const sourceNumber = Number.parseFloat(String(sourceValue));
        const numericValues = availableValues
          .map((value) => ({ value, number: Number.parseFloat(value) }))
          .filter(({ number }) => Number.isFinite(number));
        if (!Number.isFinite(sourceNumber) || numericValues.length === 0) {
          return [key, undefined];
        }

        const closestValue = numericValues.reduce((closest, current) =>
          Math.abs(current.number - sourceNumber) < Math.abs(closest.number - sourceNumber) ? current : closest,
        ).value;
        return [key, typeof sourceValue === 'number' ? Number(closestValue) : closestValue];
      })
      .filter((entry): entry is [keyof TaskSettings, unknown] => entry[1] !== undefined),
  );
  const nextSettings = validateAndCorrectSettings({
    version: targetModelVersion,
    newSettings: {
      ...targetSettings,
      ...commonSettings,
    },
    t,
    type: toFormTabType(targetMode),
  }) as TaskSettings;

  // Q2 的 img2video 不支持错峰，切过来时强制回普通队列。
  const shouldForceNormalSchedule = targetMode === 'img2video' && targetModelVersion === MODEL_VERSION.Q2;

  return {
    modelVersion: targetModelVersion,
    // HomeGenerationForm does not expose professional mode. If the target
    // form was previously used on a standalone page, treat entering home as
    // closing that mode instead of carrying a hidden professional flag.
    isProMode: false,
    [targetSettingsKey]: shouldForceNormalSchedule ? { ...nextSettings, schedule_mode: 'normal' } : nextSettings,
  } as Partial<FormParams>;
};
